/**
 * JobCardLabourLive
 * -----------------------------------------------------------------------------
 * Compact live productivity pill rendered on Kanban tiles. Shows estimated
 * hours (from line_items.quoted_labour_hours) and actual hours (computed live
 * from time_log including any currently-open session).
 *
 * Visual states:
 *   - Running, within budget          → amber + animated dot · "Xh of Yh"
 *   - Running, over budget            → rose + animated dot · "Xh / Yh (+Zm over)"
 *   - Closed, gain (actual < quoted)  → emerald · "Yh — saved 30m"
 *   - Closed, loss (actual > quoted)  → rose · "Yh — +30m over"
 *   - Closed, on track                → slate · "Yh on quote"
 *   - No quote yet, no actual yet     → ghost · "—"
 *
 * Ticks every second locally (no API calls) by recomputing minutes from the
 * open session's `started_at` field. The Kanban polling refresh of the
 * underlying card data brings in any newly-closed sessions.
 */
import { useEffect, useState, useMemo } from 'react';
import { Timer, ArrowUp, ArrowDown, CheckCircle2, PauseCircle, DollarSign } from 'lucide-react';

const fmtHM = (totalMin) => {
  if (totalMin == null || isNaN(totalMin)) return '0m';
  const m = Math.max(0, Math.round(totalMin));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem.toString().padStart(2, '0')}m`;
};

const fmtAUD = (v) => {
  const n = Math.abs(Math.round(Number(v) || 0));
  return `$${n.toLocaleString('en-AU')}`;
};

// $ loss appears the moment a job overruns by 1+ min — SM wants the dollar
// signal on every bleeding job, no threshold to hide small overruns.
const LOSS_VISIBLE_THRESHOLD_MIN = 1;
// Default Hoppers labour rate ($/hr). The exact rate lives in workshop_settings
// but for a per-tile inline pill the difference is +/- a couple dollars — not
// worth a per-card fetch. We pass it down from the parent if the consumer
// wants to override.
const DEFAULT_LABOUR_RATE = 125;

// Statuses where the clock physically pauses — the JC has logged time but the
// mechanic isn't currently turning the spanner. We show a paused pill.
// iter138.6 — `break` added so taking a lunch / smoko break renders the
// paused look identical to waiting_for_parts / sublet. Without this the
// kanban tile flashes "X on quote" while the mechanic eats lunch even
// though `_pause_active_jc_for_mechanic` already closed the time_log
// session server-side.
const PAUSED_STATUSES = new Set(['blocked', 'waiting_for_parts', 'break']);
const ACTIVE_STATUS = 'in_progress';

export default function JobCardLabourLive({ card, compact = false, labourRate = DEFAULT_LABOUR_RATE }) {
  const [_tick, setTick] = useState(0);
  const lineItems = card?.line_items || [];
  const timeLog = card?.time_log || [];
  const status = card?.status;

  const quotedMin = useMemo(
    () => lineItems.reduce((s, li) => s + (Number(li.quoted_labour_hours) || 0), 0) * 60,
    [lineItems],
  );

  const backendOpenSession = useMemo(
    () => timeLog.some(s => !s.ended_at && s.started_at),
    [timeLog],
  );

  // The CLOCK is only considered live when (a) the backend has an open
  // session AND (b) the JC is in_progress. If the SM moved the card to
  // blocked / waiting_for_parts / sm_review without the backend closing the
  // session yet (next Kanban poll), we still want the UI to look paused.
  const isLive = backendOpenSession && status === ACTIVE_STATUS;
  const isPaused = (status && PAUSED_STATUSES.has(status));
  const isClosedStatus = status === 'sm_review' || status === 'completed' || status === 'delivered';

  // Ticker only while clock is actually live
  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isLive]);

  const actualMin = useMemo(() => {
    const now = Date.now();
    // Stale-session cap: if a mechanic forgot to clock out / move the JC
    // to a closed state, the open session would otherwise keep growing
    // forever (we've seen sessions running for 5+ days on test data).
    // Cap any single open session to MAX_OPEN_SESSION_MIN (12h) so the
    // kanban dollar-loss pill doesn't drift into absurd territory.
    const MAX_OPEN_SESSION_MIN = 12 * 60;
    // If the JC has progressed beyond active work (sm_review / completed /
    // delivered) freeze the counter at the earliest end-of-work boundary
    // we know — submitted_for_review_at, then completed_at, then updated_at.
    // This safely caps any session whose ended_at is still null in legacy
    // records so the kanban loss/gain pill stops growing once the SM
    // approves the job.
    const closedCapTs = (() => {
      if (!isClosedStatus) return null;
      const cands = [
        card?.submitted_for_review_at,
        card?.completed_at,
        card?.updated_at,
      ].filter(Boolean);
      if (!cands.length) return null;
      const times = cands.map(t => new Date(t).getTime()).filter(n => !isNaN(n));
      return times.length ? Math.min(...times) : null;
    })();

    let total = 0;
    for (const s of timeLog) {
      if (!s.started_at) continue;
      const start = new Date(s.started_at).getTime();
      let end;
      if (s.ended_at) {
        end = new Date(s.ended_at).getTime();
      } else if (closedCapTs) {
        // JC is closed but this session was never closed in DB — cap at
        // the moment the SM acted (submit-for-review / completion).
        end = closedCapTs;
      } else if (isLive) {
        // Live & open. Cap at MAX_OPEN_SESSION_MIN past start so a
        // forgotten clock-out doesn't bleed the dollar pill forever.
        end = Math.min(now, start + MAX_OPEN_SESSION_MIN * 60 * 1000);
      } else if (card?.updated_at) {
        end = new Date(card.updated_at).getTime();
      } else {
        end = now;
      }
      total += Math.max(0, (end - start) / 60000);
    }
    return total;
     
  }, [timeLog, _tick, isLive, status, card?.updated_at, card?.submitted_for_review_at, card?.completed_at]);

  // Nothing logged AND no quote → render nothing (don't pollute the tile).
  if (actualMin === 0 && quotedMin === 0 && !isLive) {
    return null;
  }
  // Nothing logged yet but a quote exists, and the job isn't live or paused →
  // showing "saved 1h" would be misleading for a job that simply hasn't
  // started. Suppress the pill until something is actually clocked.
  const isClosed = isClosedStatus;
  if (actualMin === 0 && !isLive && !isPaused && !isClosed) {
    return null;
  }

  const variance = actualMin - quotedMin; // > 0 means overrun
  const overBudget = quotedMin > 0 && actualMin > quotedMin;

  let tone, icon, label, testIdSuffix = '';
  if (isLive) {
    tone = overBudget
      ? 'bg-rose-100 border-rose-300 text-rose-800'
      : 'bg-amber-100 border-amber-300 text-amber-800';
    icon = <Timer className="h-3 w-3 animate-pulse" />;
    label = quotedMin > 0
      ? `${fmtHM(actualMin)} / ${fmtHM(quotedMin)}`
      : `${fmtHM(actualMin)} live`;
    testIdSuffix = '-running';
  } else if (isPaused && actualMin > 0) {
    // Paused while job is blocked / waiting for parts — keep accumulated time
    // visible so the SM knows how much labour is already burned.
    tone = 'bg-slate-200 border-slate-300 text-slate-700';
    icon = <PauseCircle className="h-3 w-3" />;
    label = quotedMin > 0
      ? `${fmtHM(actualMin)} / ${fmtHM(quotedMin)} · paused`
      : `${fmtHM(actualMin)} · paused`;
    testIdSuffix = '-paused';
  } else if (quotedMin === 0) {
    tone = 'bg-slate-100 border-slate-200 text-slate-700';
    icon = <CheckCircle2 className="h-3 w-3" />;
    label = `${fmtHM(actualMin)} logged`;
  } else if (variance < 0) {
    tone = 'bg-emerald-100 border-emerald-300 text-emerald-800';
    icon = <ArrowUp className="h-3 w-3" />;
    label = compact
      ? `${fmtHM(-variance)} saved`
      : `${fmtHM(actualMin)} · saved ${fmtHM(-variance)}`;
  } else if (variance > 0) {
    tone = 'bg-rose-100 border-rose-300 text-rose-800';
    icon = <ArrowDown className="h-3 w-3" />;
    label = compact
      ? `${fmtHM(variance)} over`
      : `${fmtHM(actualMin)} · ${fmtHM(variance)} over`;
  } else {
    tone = 'bg-slate-100 border-slate-200 text-slate-700';
    icon = <CheckCircle2 className="h-3 w-3" />;
    label = `${fmtHM(actualMin)} on quote`;
  }

  // Variance & dollar-loss signal — visible to SM the moment the job
  // overruns by 1+ min (LOSS_VISIBLE_THRESHOLD_MIN). Shows both while LIVE
  // (bleeding money in real-time) AND after the JC closes (so the loss
  // stays visible until the SM acts on it).
  const overrunMin = quotedMin > 0 ? Math.max(0, actualMin - quotedMin) : 0;
  const overrunDollars = (overrunMin / 60) * (Number(labourRate) || 0);
  const showLossPill = overrunMin >= LOSS_VISIBLE_THRESHOLD_MIN;

  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-full text-[10px] font-semibold ${tone}`}
        title={`Estimated ${fmtHM(quotedMin)} · Actual ${fmtHM(actualMin)}${
          isLive ? ' · running' : isPaused ? ' · paused (job ' + status.replace('_', ' ') + ')' : ''
        }`}
        data-testid={`jc-labour-live-${card.job_card_id}${testIdSuffix}`}
      >
        {icon}
        {label}
      </span>
      {showLossPill && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-rose-300 bg-rose-100 text-rose-800 rounded-full text-[10px] font-bold"
          title={`Over quote by ${fmtHM(overrunMin)} — lost approx ${fmtAUD(overrunDollars)} @ $${(Number(labourRate) || 0).toFixed(0)}/h`}
          data-testid={`jc-labour-loss-${card.job_card_id}`}
        >
          <DollarSign className="h-2.5 w-2.5" />
          −{fmtAUD(overrunDollars)} lost
        </span>
      )}
    </span>
  );
}
